import React, { useMemo } from 'react';
import { Modal, Box, Typography, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VenmoIcon from '@mui/icons-material/AccountBalance';
import SplitReceiptDrawer from './SplitReceiptDrawer';

const SplitReceipts = React.memo(({
  isSplitting,
  showSplitModal,
  splits,
  splitNames,
  splitItems,
  splitTip,
  splitTax,
  selectedReceipt,
  splitColors,
  items,
  handleAddSplit,
  handleFinishSplit,
  setIsSplitting,
  handleSplitNameChange,
  handleSplitTipChange,
  handleSplitTaxChange,
  handleCloseSplitModal,
  handleExportToVenmo,
}) => {
  const drawerHeight = '300px';

  const SplitResultModal = useMemo(() => () => (
    <Modal
      open={showSplitModal}
      onClose={handleCloseSplitModal}
      aria-labelledby="split-result-modal"
      aria-describedby="split-result-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <Typography id="split-result-modal" variant="h6" component="h2" gutterBottom>
          Split Result for {selectedReceipt?.merchant_name}
        </Typography>
        <Typography id="split-result-details" sx={{ mt: 2 }}>
          Total: ${parseFloat(selectedReceipt?.total_cost || 0).toFixed(2)}
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary', color: '#4caf50' }}>
          Screenshot and send these to friends to split the bill!
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Note: Always verify and sanity check generative AI parsed prices as they might be incorrect.
        </Typography>
        {splits.map((split, index) => {
          const splitColor = splitColors[index % splitColors.length];
          const splitItemsList = items.filter(item => splitItems[item.item_id] === index);
          const tipAmount = splitTip ? parseFloat(selectedReceipt?.maybe_receipt_tip || 0) / splits.length : 0;
          const taxAmount = splitTax ? parseFloat(selectedReceipt?.maybe_receipt_tax || 0) / splits.length : 0;

          return (
            <Box key={index} sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1, borderLeft: `4px solid ${splitColor}` }}>
              <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', color: splitColor }}>
                {splitNames[index]}
              </Typography>
              <Typography>
                Amount: ${split.total.toFixed(2)}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">
                  Items: {splitItemsList.length}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<VenmoIcon />}
                  onClick={() => handleExportToVenmo(index)}
                  size="small"
                  sx={{ bgcolor: splitColor, '&:hover': { bgcolor: splitColor } }}
                >
                  Export to Venmo
                </Button>
              </Box>
              <Accordion sx={{ mt: 2, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>View Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {splitItemsList.map((item) => (
                      <ListItem key={item.item_id}>
                        <ListItemText
                          primary={item.item_name}
                          secondary={`$${parseFloat(item.item_price).toFixed(2)}`}
                        />
                      </ListItem>
                    ))}
                    {tipAmount > 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Tip (Split)"
                          secondary={`$${tipAmount.toFixed(2)}`}
                        />
                      </ListItem>
                    )}
                    {taxAmount > 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Tax (Split)"
                          secondary={`$${taxAmount.toFixed(2)}`}
                        />
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
        <Button onClick={handleCloseSplitModal} sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    </Modal>
  ), [showSplitModal, handleCloseSplitModal, selectedReceipt, splits, splitNames, splitItems, splitTip, splitTax, splitColors, items, handleExportToVenmo]);

  return (
    <>
      <SplitReceiptDrawer
        isSplitting={isSplitting}
        drawerHeight={drawerHeight}
        splits={splits}
        splitNames={splitNames}
        splitTip={splitTip}
        splitTax={splitTax}
        selectedReceipt={selectedReceipt}
        splitColors={splitColors}
        handleAddSplit={handleAddSplit}
        handleFinishSplit={handleFinishSplit}
        setIsSplitting={setIsSplitting}
        handleSplitNameChange={handleSplitNameChange}
        handleSplitTipChange={handleSplitTipChange}
        handleSplitTaxChange={handleSplitTaxChange}
      />
      <SplitResultModal />
    </>
  );
});

export default SplitReceipts;
